import { Loader2, RotateCcw, Save } from 'lucide-react';
import { type ReactNode } from 'react';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/cn';

/**
 * Pinned to the bottom of the viewport while a form has unsaved edits.
 *
 * A long page scrolls well past a Save button parked at the top, so the button
 * has to come to the user rather than the other way round. It also doubles as
 * the honest answer to "did that take?" — previously several settings screens
 * had no dirty indication at all, so the only way to know whether you'd changed
 * something was to remember.
 *
 * Callers that render this must reserve space at the bottom of the page
 * (`pb-24`) or the bar covers the last row of content.
 */
export function SaveBar({
  visible,
  /** What changed, e.g. "3 permission changes". Keep it countable. */
  summary,
  /** Who or what the changes apply to. Rendered muted after the summary. */
  context,
  isSaving,
  saveLabel = 'Save changes',
  onSave,
  onDiscard,
  className,
}: {
  visible: boolean;
  summary: ReactNode;
  context?: ReactNode;
  isSaving?: boolean;
  saveLabel?: string;
  onSave: () => void;
  onDiscard?: () => void;
  className?: string;
}) {
  if (!visible) return null;

  return (
    <div
      role="region"
      aria-label="Unsaved changes"
      className={cn(
        // `lg:left-60` clears the sidebar rail so the bar doesn't sit on top of
        // the navigation on desktop.
        'fixed inset-x-0 bottom-0 z-40 border-t border-border bg-surface-elevated/95 px-4 py-3 shadow-kpi backdrop-blur lg:left-60',
        className,
      )}
    >
      <div className="mx-auto flex max-w-[1600px] flex-wrap items-center gap-3">
        <span className="inline-block h-2 w-2 shrink-0 animate-pulse rounded-full bg-primary" aria-hidden />
        <p className="min-w-0 text-xs">
          <span className="font-medium">{summary}</span>
          {context ? <span className="text-muted-foreground"> {context}</span> : null}
        </p>
        <div className="ml-auto flex items-center gap-2">
          {onDiscard ? (
            <Button size="sm" variant="ghost" onClick={onDiscard} disabled={isSaving}>
              <RotateCcw className="h-3.5 w-3.5" aria-hidden /> Discard
            </Button>
          ) : null}
          <Button size="sm" variant="primary" onClick={onSave} disabled={isSaving}>
            {isSaving ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
            ) : (
              <Save className="h-3.5 w-3.5" aria-hidden />
            )}
            {saveLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}

/** "3 changes" / "1 change" — the phrasing every save bar needs. */
export function pluralizeChanges(count: number, noun = 'change'): string {
  return `${count} ${noun}${count === 1 ? '' : 's'}`;
}
